/**
 * Authentication Context
 * Manages authentication state and provides auth methods to the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    AuthUser,
    AuthState,
    SecurityLevel,
    UserRole,
    Permission,
    LoginResponse,
    UserType
} from '../data/Auth.types';
import { getUserPermissions } from '../config/Permissions.config';
import * as Const from '../Constants';

var qs = require('qs');

interface AuthContextType extends AuthState {
    login: (username: string, password: string, secretWord?: string, secretSequence?: string[]) => Promise<{ success: boolean; requiresSecretWord?: boolean; secretSequence?: string[]; error?: string }>;
    logout: () => Promise<boolean>;
    checkSession: () => Promise<boolean>;
    hasPermission: (permission: Permission) => boolean;
    hasAnyPermission: (permissions: Permission[]) => boolean;
    hasAllPermissions: (permissions: Permission[]) => boolean;
    hasRole: (role: UserRole) => boolean;
    canAccessLevel: (level: SecurityLevel) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {

    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,
        requestedSecretWord: false,
        secretWordSequence: null,
        hintQuestion: null,
        userName: null

    });

    /**
     * Parse roles from API response
     */
    const parseRoles = (roles?: string[]): UserRole[] => {
        if (!roles || roles.length === 0) return [];

        return roles
            .map(role => {
                // Match role string to UserRole enum
                const roleKey = Object.keys(UserRole).find(
                    key => UserRole[key as keyof typeof UserRole] === role
                );
                return roleKey ? UserRole[roleKey as keyof typeof UserRole] : null;
            })
            .filter((role): role is UserRole => role !== null);
    };

    /**
     * Determine security level from user type and roles
     */
    const getSecurityLevel = (userType: 'c' | 'i', roles: UserRole[]): SecurityLevel => {
        if (userType === 'i' && roles.length > 0) {
            return SecurityLevel.INTERNAL;
        }
        if (userType === 'c') {
            return SecurityLevel.CLIENT;
        }
        return SecurityLevel.PUBLIC;
    };

    const useQuery = () => {
        return qs.parse(window.location.search);
    };
    /**
     * Fetch user metadata after authentication
     */
    const fetchUserMetadata = async (sessionId: string): Promise<any> => {
        try {
            const response = await fetch(
                `${Const.API_GET_USER_META_INFO}?usersession=${sessionId}`,
                {
                    method: 'GET',
                    headers: {
                        'accept-language': localStorage?.getItem('rcml-lang') || 'ar'
                    }
                }
            );

            const data = await response.json();

            if (data.result === 1 && data.response?.data) {
                return data.response.data;
            }

            return null;
        } catch (error) {
            console.error('Error fetching user metadata:', error);
            return null;
        }
    };

    /**
     * Login user with username and password (step 1)
     */
    const requestSecretWord = async (
        username: string,
        password: string,
        lang: string = 'en'
    ): Promise<{ success: boolean; secretSequence?: string[], hint?: string, error?: string }> => {
        try {
            const formData = new URLSearchParams();
            formData.append('username', username);
            formData.append('password', password);
            formData.append('lang', lang);

            const response = await fetch(Const.API_GET_SECRET_WORD, {
                method: 'POST',
                headers: {
                    'accept-language': lang,
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: formData.toString()
            });

            const data = await response.json();

            if (data.result === 1) {
                return { success: true, secretSequence: data.response?.data.requiredLetter, hint: data.response?.data.userHint };
            } else {
                return {
                    success: false,
                    error: data.response?.message || 'Invalid username or password'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    /**
     * Submit secret word (step 2) and complete login
     */
    const submitSecretWord = async (
        username: string,
        secretWord: string,
        secretSequence: string[],
        lang: string = 'en'
    ): Promise<{ success: boolean; sessionId?: string; userType?: UserType; error?: string }> => {
        try {
            const formData = new URLSearchParams();
            formData.append('username', username);
            formData.append('secretword', secretWord);
            formData.append('sequence', secretSequence.join(','));

            const response = await fetch(Const.API_GET_USER_SESSION, {
                method: 'POST',
                headers: {
                    'accept-language': lang,
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: formData.toString()
            });

            const data: LoginResponse = await response.json();

            if (data.result === 1 && data.response?.data?.userSession) {
                return {
                    success: true,
                    sessionId: data.response.data?.userSession,
                    userType: data.response.data?.usertype
                };
            } else {
                return {
                    success: false,
                    error: data.response?.message || 'Invalid secret word'
                };
            }
        } catch (error) {
            console.error('Secret word submission error:', error);
            return {
                success: false,
                error: 'Network error. Please try again.'
            };
        }
    };

    /**
     * Complete login flow
     */
    const login = async (
        username: string,
        password: string,
        secretWord?: string,
        enterSecretSequence?: string[]
    ): Promise<{ success: boolean; error?: string }> => {

        setState(prev => ({ ...prev, isLoading: true, error: null })); //, user: { ...prev.user, sessionId: '' }

        try {
            // Step 1: If no secret word provided, request it
            if (!secretWord) {
                const result = await requestSecretWord(
                    username,
                    password,
                    localStorage?.getItem('rcml-lang') || 'ar'
                );

                if (result.success) {
                    setState(prev => ({ ...prev, requestedSecretWord: result.success, secretWordSequence: result?.secretSequence, hintQuestion: result?.hint, userName: username, isLoading: false }));
                    return { success: true };
                } else {
                    setState(prev => ({ ...prev, isLoading: false, error: result.error || null }));
                    return { success: false, error: result.error };
                }
            }

            // Step 2: Submit secret word and get session
            if (!enterSecretSequence) {
                throw new Error('Secret sequence is required');
            }

            const secretResult = await submitSecretWord(
                state.userName,
                secretWord,
                enterSecretSequence,
                localStorage?.getItem('rcml-lang') || 'ar'
            );

            if (!secretResult.success || !secretResult.sessionId) {
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                    error: secretResult.error || null
                }));
                return { success: false, error: secretResult.error };
            }

            //if (secretResult.success && secretResult.sessionId) { 
            //    setState(prev => ({
            //        ...prev,
            //        user: {
            //            ...prev.user,
            //            sessionId: secretResult?.sessionId,
            //            userType: secretResult?.userType
            //        },
            //        isLoading: true,
            //        error: secretResult.error || null
            //    }));
            //}                

            // Step 3: Fetch user metadata
            const metadata = await fetchUserMetadata(secretResult.sessionId);

            // Step 4: Fetch user watchlist
            const _watchlist = await fetchWatchlist(secretResult.sessionId);

            if (!metadata) {
                setState(prev => ({
                    ...prev,
                    isLoading: false,
                    error: 'Failed to fetch user data'
                }));
                return { success: false, error: 'Failed to fetch user data' };
            }

            // Step 5: Determine user type and roles from API response
            const userType: UserType = secretResult?.userType || 'c';
            const userRoles = (metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? [UserRole.SUPER_ADMIN] : parseRoles(metadata.roles);//[UserRole.CLIENT]; //(metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? [UserRole.SUPER_ADMIN] : parseRoles(metadata.roles); //parseRoles(metadata.roles)
            const securityLevel = (metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? SecurityLevel.INTERNAL : SecurityLevel.CLIENT;//SecurityLevel.CLIENT; //(metadata.logedCustomerinfo?.cust_acc_code == "000000000032042") ? SecurityLevel.CLIENT : (metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? SecurityLevel.INTERNAL : SecurityLevel.CLIENT; //getSecurityLevel(userType, userRoles);
            const permissions = getUserPermissions({ securityLevel: securityLevel, roles: userRoles });

            // Step 6: Create authenticated user object
            const authUser: AuthUser = {
                id: metadata.logedCustomerinfo?.cust_acc_code || username,
                username,
                sessionId: secretResult.sessionId,
                securityLevel,
                roles: userRoles,
                permissions,
                mainAccount: metadata.logedCustomerinfo,
                otherAccounts: metadata.otherAccounts || [],
                authenticated: true,
                userType,
                marginUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 1) ? true : false,
                sameDayUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 2) ? true : false,
                treasuryBillsUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 3) ? true : false,
                mutualFundsUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 4) ? true : false,
                isDemoAccount: metadata.logedCustomerinfo?.isDemoAccount == 'Y',
                websiteWatchlist: _watchlist || []
            };

            // Step 6: Update state
            setState((prev) => ({
                ...prev,
                user: authUser,
                isAuthenticated: true,
                isLoading: false,
                error: null
            }));

            // Store session info
            localStorage.setItem('sessionId', secretResult.sessionId);
            localStorage.setItem('securityLevel', securityLevel.toString());

            return { success: true };
        } catch (error) {
            console.error('Login error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Login failed';

            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage
            }));

            return { success: false, error: errorMessage };
        }
    };

    const fetchWatchlist = async (sessionId: string): Promise<any> => {


        try {
            const response = await fetch(
                `${Const.API_GET_USER_WATCHLIST}?usersession=${sessionId}`,
                {
                    method: 'GET',
                    headers: {
                        'accept-language': localStorage?.getItem('rcml-lang') || 'ar'
                    }
                }
            );

            const data = await response.json();

            if (data?.result === 1 && data.response?.data) {
                return data.response.data.configurations;
            } else {
                return Const.WATCHLIST_DATA.response.data.lists;
            }

            return null;
        } catch (error) {
            console.error('Error fetching user watchlist:', error);
            return null;
        }

    }

    /**
     * Logout user
     */
    const logout = async (): Promise<boolean> => {
        try {
            // Call logout endpoint if needed
            await fetch((Const.API_SET_USER_LOGOUT + `?usersession=${state?.user?.sessionId}`) as string)
                .then((response) => response.json())
                .then((json) => {
                    if (json.result == 1) {

                        // Clear state
                        setState({
                            user: {
                                authenticated: false,
                                permissions: [],
                                id: '',
                                sessionId: '',
                                isDemoAccount: false,
                                roles: [],
                                securityLevel: SecurityLevel.PUBLIC,
                                username: '',
                                userType: null,
                                mainAccount: null,
                                marginUser: false,
                                mutualFundsUser: false,
                                otherAccounts: [],
                                sameDayUser: false,
                                treasuryBillsUser: false,
                                websiteWatchlist: []
                            },
                            isAuthenticated: false,
                            isLoading: false,
                            error: null,
                            requestedSecretWord: false,
                            secretWordSequence: null,
                            hintQuestion: null,
                            userName: null
                        });

                        // Clear storage
                        localStorage.removeItem('sessionId');
                        localStorage.removeItem('securityLevel');

                        return true;

                    } else {
                        console.error('Unknow error');
                        return false;
                    }

                })
                .catch((error) => {
                    console.error('Logout error:', error);
                    return false;
                });

        } catch (error) {
            console.error('Logout error:', error);
            return false;
        }
    };

    /**
     * Check if session is still valid
     */
    const checkSession = async (): Promise<boolean> => {

        const sessionId = useQuery()['?sessionid'] || localStorage.getItem('sessionId') || '';

        if (!sessionId) {
            setState(prev => ({ ...prev, user: { ...prev.user, sessionId: '' }, isLoading: false }));
            return false;
        }

        try {
            const response = await fetch(`${Const.USER_UPDATE_SESS}${sessionId}`, {
                method: 'GET'
            });

            if (response.ok) {
                const text = await response.text();

                if (text.includes('Y')) {
                    // Session is valid, restore user state
                    const metadata = await fetchUserMetadata(sessionId);

                    const _watchlist = await fetchWatchlist(sessionId);

                    if (metadata) {
                        const userType: 'c' | 'i' = metadata.usertype || 'c';
                        const userRoles = (metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? [UserRole.SUPER_ADMIN] : parseRoles(metadata.roles); //parseRoles(metadata.roles)
                        const securityLevel = (metadata.logedCustomerinfo?.cust_acc_code == "000000000032042") ? SecurityLevel.CLIENT : (metadata.logedCustomerinfo?.cust_acc_code == "000000000029462") ? SecurityLevel.INTERNAL : SecurityLevel.CLIENT; //getSecurityLevel(userType, userRoles);
                        const permissions = getUserPermissions({ securityLevel: securityLevel, roles: userRoles }); //getUserPermissions(securityLevel, userRoles);

                        const authUser: AuthUser = {
                            id: metadata.logedCustomerinfo?.cust_acc_code,
                            username: "",
                            sessionId: sessionId,
                            securityLevel,
                            roles: userRoles,
                            permissions,
                            mainAccount: metadata.logedCustomerinfo,
                            otherAccounts: metadata.otherAccounts || [],
                            authenticated: true,
                            userType,
                            marginUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 1) ? true : false,
                            sameDayUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 2) ? true : false,
                            treasuryBillsUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 3) ? true : false,
                            mutualFundsUser: metadata.logedCustomerinfo?.acc_type?.find((v: any) => v == 4) ? true : false,
                            isDemoAccount: metadata.logedCustomerinfo?.isDemoAccount == 'Y',
                            websiteWatchlist: _watchlist || []
                        };

                        setState((prev) => ({
                            ...prev,
                            user: authUser,
                            isAuthenticated: true,
                            isLoading: false,
                            error: null
                        }));

                        // Store session info
                        localStorage.setItem('sessionId', sessionId);
                        localStorage.setItem('securityLevel', securityLevel.toString());

                        return true;
                    }
                }
            }

            // Session invalid
            await logout();
            return false;
        } catch (error) {
            console.error('Session check error:', error);
            await logout();
            return false;
        }
    };

    /**
     * Permission checking methods
     */
    const hasPermission = useCallback(
        (permission: Permission): boolean => {
            return state.user?.permissions.includes(permission) || false;
        },
        [state.user]
    );

    const hasAnyPermission = useCallback(
        (permissions: Permission[]): boolean => {
            return permissions.some(perm => state.user?.permissions.includes(perm)) || false;
        },
        [state.user]
    );

    const hasAllPermissions = useCallback(
        (permissions: Permission[]): boolean => {
            return permissions.every(perm => state.user?.permissions.includes(perm)) || false;
        },
        [state.user]
    );

    const hasRole = useCallback(
        (role: UserRole): boolean => {
            return state.user?.roles.includes(role) || false;
        },
        [state.user]
    );

    const canAccessLevel = useCallback(
        (level: SecurityLevel): boolean => {
            return (state.user?.securityLevel || SecurityLevel.PUBLIC) >= level;
        },
        [state.user]
    );

    /**
     * Check session on mount
     */
    useEffect(() => {
        checkSession();
    }, []);

    /**
     * Setup session refresh interval (every 5 minutes)
     */
    useEffect(() => {
        if (!state.isAuthenticated) return;

        const interval = setInterval(() => {
            checkSession();
        }, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(interval);
    }, [state.isAuthenticated]);

    const value: AuthContextType = {
        ...state,
        login,
        logout,
        checkSession,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        canAccessLevel
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

export default AuthContext;